import { getBusinessDateString } from './business-date.ts';
export interface ReportEventLoan {
  channel?: string | null;
  broker_fund_date?: string | null;
  funded_date?: string | null;
  creation_date?: string | null;
  application_date?: string | null;
  purchased_date?: string | null;
  rate_lock_buy_side_lock_date?: string | null;
  rate_lock_sell_side_lock_date?: string | null;
  rate_lock_request_lock_date?: string | null;
}


export const CANONICAL_EVENT_KEYS = ['created', 'application', 'locked', 'funded', 'purchased'] as const;

export type CanonicalEventKey = typeof CANONICAL_EVENT_KEYS[number];

export type CanonicalEventDates = Record<CanonicalEventKey, string | null>;

export interface ReportableLoanFields {
  loan_folder?: string | null;
  deleted_from_encompass?: boolean | null;
  deleted?: boolean | null;
}

export const EXCLUDED_REPORT_FOLDERS: ReadonlySet<string> = new Set([
  'Test',
  '(Trash)',
  'Transferred Data'
]);

/**
 * Server-side transport reducer for the loans query, mirroring
 * isReportableLoan's folder exclusion. Null-safe on purpose: a NULL
 * loan_folder is reportable, and a bare `not.in` would drop it.
 * isReportableLoan stays authoritative on the fetched rows.
 */
export const REPORTABLE_FOLDER_FILTER = `loan_folder.is.null,loan_folder.not.in.(${
  [...EXCLUDED_REPORT_FOLDERS].map((folder) => `"${folder}"`).join(',')
})`;
const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MIDNIGHT_UTC_PATTERN = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d+)?(?:Z|[+-]00(?::?00)?)$/i;

function isValidDateKey(value: string): boolean {
  const match = value.match(DATE_KEY_PATTERN);
  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

/**
 * Convert an Encompass event value to its inclusive Pacific calendar key.
 *
 * PostgreSQL DATE values and legacy midnight-UTC representations are calendar
 * dates, not instants, so their leading date is preserved. Timestamps carrying
 * a real time are converted to America/Los_Angeles before extracting the day.
 */
export function toPacificDateKey(value: string | Date | null | undefined): string | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : getBusinessDateString(value);
  }

  const trimmed = value.trim();
  if (DATE_KEY_PATTERN.test(trimmed)) {
    return isValidDateKey(trimmed) ? trimmed : null;
  }

  const midnightUtc = trimmed.match(MIDNIGHT_UTC_PATTERN);
  if (midnightUtc) {
    return isValidDateKey(midnightUtc[1]) ? midnightUtc[1] : null;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return getBusinessDateString(parsed);
}

export function getCanonicalLockDate(loan: ReportEventLoan): string | null {
  return toPacificDateKey(
    loan.rate_lock_buy_side_lock_date
      || loan.rate_lock_sell_side_lock_date
      || loan.rate_lock_request_lock_date
      || null
  );
}

/**
 * Canonical funded semantics shared by historical reports.
 * Brokered loans prefer a matched payout fund date. Every channel falls back
 * to the actual funded milestone field; closing_date is intentionally excluded.
 */
export function getCanonicalFundedDate(loan: ReportEventLoan): string | null {
  const value = loan.channel === 'Brokered' && loan.broker_fund_date
    ? loan.broker_fund_date
    : loan.funded_date;
  return toPacificDateKey(value);
}

export function getCanonicalEventDate(
  loan: ReportEventLoan,
  event: CanonicalEventKey
): string | null {
  switch (event) {
    case 'created':
      return toPacificDateKey(loan.creation_date);
    case 'application':
      return toPacificDateKey(loan.application_date);
    case 'locked':
      return getCanonicalLockDate(loan);
    case 'funded':
      return getCanonicalFundedDate(loan);
    case 'purchased':
      return toPacificDateKey(loan.purchased_date);
  }
}

export function getCanonicalEventDates(loan: ReportEventLoan): CanonicalEventDates {
  return {
    created: getCanonicalEventDate(loan, 'created'),
    application: getCanonicalEventDate(loan, 'application'),
    locked: getCanonicalEventDate(loan, 'locked'),
    funded: getCanonicalEventDate(loan, 'funded'),
    purchased: getCanonicalEventDate(loan, 'purchased')
  };
}

export function isDateKeyWithinInclusiveRange(
  dateKey: string | null | undefined,
  from: string,
  to: string
): boolean {
  return Boolean(dateKey && from <= dateKey && dateKey <= to);
}

/** Historical reporting population: all non-deleted loans outside system folders. */
export function isReportableLoan(loan: ReportableLoanFields): boolean {
  const folder = loan.loan_folder?.trim() || '';
  return loan.deleted_from_encompass !== true
    && loan.deleted !== true
    && !EXCLUDED_REPORT_FOLDERS.has(folder);
}
